'use client';

import { motion } from 'framer-motion';
import { User } from 'lucide-react';
import React from 'react';

import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';
import { cn } from '@/ux/utils';

interface BlogMetadata {
  boardId: string;
  name: string;
  description: string;
  displayImage: string;
  accessType: 'NONE' | 'EMAIL' | 'CODE';
  isPasswordRequired: boolean;
  createdBy: string;
  creatorInfo: {
    Name: string;
    Email: string;
    DisplayPic: string;
    Handle: string;
    Headline: string;
    Id: string;
  };
  isMember: boolean;
  isExclusive: boolean;
  summary: string;
  questions: string[];
  shareId: string;
}

interface BlogItem {
  id: string;
  url: string;
}

interface BlogTileAtmosphericProps {
  blog: BlogItem;
  metadata?: BlogMetadata | null;
  onClick: () => void;
  loading?: boolean;
  isUserLoggedIn?: boolean;
  index?: number;
}

// Determine blog access type for visual styling
function getBlogAccessType(metadata: BlogMetadata | null, isUserLoggedIn: boolean) {
  if (!metadata) return 'unknown';

  if (metadata.isExclusive) return 'member-exclusive';
  if (metadata.accessType === 'CODE') return 'code-protected';
  if (metadata.accessType === 'EMAIL') {
    return isUserLoggedIn && metadata.isMember ? 'email-authenticated' : 'email-protected';
  }
  return 'public';
}

export const BlogTileAtmospheric: React.FC<BlogTileAtmosphericProps> = ({
  blog,
  metadata,
  onClick,
  loading = false,
  isUserLoggedIn = false,
  index = 0,
}) => {
  if (loading) {
    return (
      <motion.div
        className="blog-card-atmospheric"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
      >
        <div className="blog-card-skeleton blog-card-image-container" />
        <div className="blog-card-content">
          <div className="blog-card-skeleton h-6 w-3/4 rounded mb-3" />
          <div className="blog-card-skeleton h-4 w-full rounded mb-2" />
          <div className="blog-card-skeleton h-4 w-2/3 rounded mb-4" />
          <div className="flex items-center gap-3">
            <div className="blog-card-skeleton w-10 h-10 rounded-full" />
            <div className="flex-1">
              <div className="blog-card-skeleton h-4 w-24 rounded mb-1" />
              <div className="blog-card-skeleton h-3 w-32 rounded" />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (!metadata) {
    return (
      <motion.div
        className="blog-card-atmospheric opacity-50"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 0.5, y: 0 }}
        transition={{ delay: index * 0.1 }}
      >
        <div className="blog-card-image-container flex items-center justify-center bg-gray-100">
          <span className="text-gray-400 text-sm">Blog unavailable</span>
        </div>
      </motion.div>
    );
  }

  const accessType = getBlogAccessType(metadata, isUserLoggedIn);

  // Access type configurations
  const accessConfigs = {
    public: {
      indicator: 'Open Access',
      className: 'access-open',
    },
    'email-protected': {
      indicator: 'Sign in to view',
      className: 'access-email',
    },
    'email-authenticated': {
      indicator: 'You have access',
      className: 'access-authenticated',
    },
    'code-protected': {
      indicator: 'Code Required',
      className: 'access-code',
    },
    'member-exclusive': {
      indicator: 'Members Only',
      className: 'access-member',
    },
    unknown: {
      indicator: '',
      className: '',
    },
  };

  const accessConfig = accessConfigs[accessType as keyof typeof accessConfigs];

  return (
    <motion.article
      className="blog-card-atmospheric"
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Atmosphere Layer */}
      <div className="blog-card-atmosphere" />

      {/* Access Indicator */}
      {accessConfig.indicator && (
        <div className={cn('access-indicator', accessConfig.className)}>{accessConfig.indicator}</div>
      )}

      {/* Hero Image */}
      <div className="blog-card-image-container">
        {metadata.displayImage ? (
          <>
            <img
              src={appendImageRepoDomainIfNotExistInUrl(metadata.displayImage)}
              alt={metadata.name}
              className="blog-card-image"
              loading="lazy"
            />
            <div className="blog-card-image-overlay" />
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <div className="text-6xl opacity-20">📄</div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="blog-card-content">
        <h3 className="blog-card-title line-clamp-2">{metadata.name}</h3>

        {metadata.description && <p className="blog-card-description">{metadata.description}</p>}

        {/* Author Info */}
        {metadata.creatorInfo && (
          <div className="blog-card-author">
            {metadata.creatorInfo.DisplayPic ? (
              <img
                src={appendImageRepoDomainIfNotExistInUrl(metadata.creatorInfo.DisplayPic)}
                alt={metadata.creatorInfo.Name}
                className="blog-card-avatar"
              />
            ) : (
              <div className="blog-card-avatar bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                <User className="w-5 h-5 text-gray-500" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{metadata.creatorInfo.Name}</p>
              {metadata.creatorInfo.Headline && (
                <p className="text-xs opacity-70 truncate">{metadata.creatorInfo.Headline}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.article>
  );
};
